import { Form } from 'react-final-form';
import { css } from '@emotion/react';
import { Box, CircularProgress, Grid, useMediaQuery } from '@material-ui/core';

import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Questions, QuestionsTypeEnum } from 'types/questions';
import Alert from 'components/shared/Alert/Alert';
import { i18n } from 'lib/i18n';
import { applicationApi } from 'lib/api/applicationApi';
import { Conditional, FormUtils } from '../Checkout/program/step/lib/application.util';

import QuestionnaireControl from 'components/shared/FormControls/QuestionnaireControl';
import { Answer } from 'types/answer';

import RowLayout from 'layouts/Row';

import Button from 'components/shared/Button/Button';
import LoginDialog from 'components/shared/Dialog/LoginDialog';

import { UserSettings } from 'types/userSettings';

import { useRouter } from 'next/router';

import FamilyMembers from 'components/shared/FamilyMembers/FamilyMembers';
import { desktopUp, mobileOnly } from 'styles/utils';

import { ModalWindow } from 'bondsports-utils';
import ProfileDetailsChangedAlert from 'components/shared/ProfileDetails/ProfileDetailsChangedAlert';
import { useUpdateProfile } from 'components/shared/ProfileDetails/useUpdateProfile';
import { userApi } from 'lib/api/userApi';
import { useMemberAuthorization } from 'hooks/ueMeusemberAuthorization';
import { dialogCss } from 'components/Checkout/styles';
import { useLogin } from 'hooks/useLogin';
import { auth } from 'lib/auth';

const titleCss = (colors: IColors) => css`
	font-size: 2.4rem;
	font-weight: bold;
	color: ${colors.tx_text_primary};
`;
const acceptBtnLayoutCss = css`
	margin-top: 40px;
	justify-content: flex-end;
	display: flex;

	${mobileOnly} {
		justify-content: center;
	}
`;
const acceptBtnCss = css`
	&.MuiButtonBase-root {
		height: 45px;
	}
`;

const questionFormDivCss = css`
	display: grid;
`;

const disabledCss = (isNoSelectedMember: boolean) => css`
	pointer-events: ${isNoSelectedMember && 'none'};

	a {
		pointer-events: all !important;
		text-decoration: underline;
	}
`;

export interface Props {
	waiverFilled: (selectedMember?: UserSettings | null) => void;
	orgQuestionairreId?: number;
	onSetTitle: (title: string) => void;
	formTitle?: string;
	onLogIn?: () => void;
}

const prefix = 'question_';

const WaiverContent = ({ waiverFilled, onSetTitle, orgQuestionairreId, formTitle, onLogIn }: Props) => {
	const { colors } = useColors();

	const isDesktop = useMediaQuery(desktopUp);

	const router = useRouter();
	const { organizationId, formId } = router.query;
	const [questionData, setQuestionData] = useState<Questions[]>();
	const [termsQuestions, setTermsQuestions] = useState<Questions[]>();
	const [isFormInvalid, setIsFormInvalid] = useState(false);
	const [selectedMember, setSelectedMember] = useState<UserSettings | null>(null);
	const [isNoSelectedMemberError, setIsNoSelectedMemberError] = useState(false);

	const updateSelectedMember = (member: UserSettings | null) => {
		setSelectedMember(member);
	};

	const { getIsSelectedMemberTheUserByMember } = useMemberAuthorization();
	const isSelectedMemberTheUser = getIsSelectedMemberTheUserByMember(selectedMember);

	const {
		userSettings,
		setUserSettings,
		newUserSettings,
		setNewUserSettings,
		updateProfile,
		chechIsProfileFieldsChangedInWaiver,
		isAlertProfileChanged,
	} = useUpdateProfile();

	const { openLoginDialog, handlePostSignup, handleLoginDialogClose, isLoggedIn } = useLogin();

	useEffect(() => {
		if (!isLoggedIn) {
			setSelectedMember(null);
		} else {
			onLogIn?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoggedIn]);

	useEffect(() => {
		if (!selectedMember) {
			setNewUserSettings(null);
			return;
		}

		setNewUserSettings(
			isSelectedMemberTheUser
				? userSettings
				: ({
						birthDate: selectedMember?.birthDate,
						address: userSettings?.address,
						phoneNumber: userSettings?.phoneNumber,
				  } as UserSettings)
		);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedMember]);

	useEffect(() => {
		const controller = new AbortController();

		if (!organizationId) {
			return;
		}

		(async () => {
			if (isLoggedIn && (formId || orgQuestionairreId)) {
				const { noWaivers, termsQuestions, title } = await FormUtils.fetchQuestionData(
					Number(organizationId),
					Number(formId ?? orgQuestionairreId)
				);
				onSetTitle(title);
				setQuestionData(noWaivers);
				setTermsQuestions(termsQuestions);
			}
		})();

		return function cleanup() {
			controller.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formId, organizationId, orgQuestionairreId, setQuestionData, setTermsQuestions, isLoggedIn]);

	let validateForm: () => Promise<unknown> | undefined;

	/* eslint-disable require-await */
	const validate = async () => {
		if (!(await validateForm()) || !selectedMember) {
			setIsFormInvalid(true);
			return false;
		}
	};

	const handleAcceptBtnSubmit = async () => {
		if (!(await validate()) || !selectedMember) {
			window.scrollTo(0, 0);
			return false;
		}
	};

	const handleSubmitForm = async (formValues: { [x: string]: never }) => {
		if (!selectedMember) {
			return;
		}

		let valid = true;

		const multiChoicesArr: Questions[] = questionData
			? questionData.reduce((accumulator: Questions[], questionItem) => {
					if (questionItem.metaData && questionItem.metaData.customType === 'multipleChoices') {
						accumulator.push(questionItem);
					}

					return accumulator;
			  }, [])
			: [];

		for (const element of multiChoicesArr) {
			if (element.isMandatory && !formValues[`${prefix}${element.id}`]) {
				valid = false;
				break;
			}
		}

		if (!valid) {
			return false;
		}

		let addressObj = {
			questionId: 0,
			street: '',
			city: '',
			state: '',
			zip: '',
		};
		const selectedAnswers: Answer[] = [];

		for (const formField of Object.keys(formValues)) {
			const [fieldName, sQuestionId] = formField.split('_');
			const questionId = Number(sQuestionId);
			const fieldValue = formValues[formField];

			switch (fieldName) {
				case 'date':
					selectedAnswers.push({
						questionId,
						value: format(fieldValue, 'yyyy-MM-dd'),
					});
					break;

				case 'userAddressStreet':
					addressObj = { ...addressObj, questionId, street: fieldValue };
					break;

				case 'userAddressZip':
					addressObj = { ...addressObj, questionId, zip: fieldValue };
					break;

				case 'userAddressCity':
					addressObj = { ...addressObj, questionId, city: fieldValue };
					break;

				case 'userAddressState':
					addressObj = { ...addressObj, questionId, state: fieldValue };
					break;

				default:
					if (Array.isArray(fieldValue)) {
						selectedAnswers.push({
							questionId,
							value: { value: [...fieldValue] },
						});
					} else {
						selectedAnswers.push({
							questionId,
							value: { value: fieldValue },
						});
						break;
					}
			}
		}

		if (addressObj.questionId) {
			const zip = addressObj.zip;
			selectedAnswers.push({
				questionId: addressObj.questionId,
				value: {
					street: addressObj.street,
					city: addressObj.city,
					state: addressObj.state,
					zip,
				},
			});
		}

		const data = {
			answers: selectedAnswers,
			userId: selectedMember.id,
		};

		await applicationApi.saveApplications(data);

		const isProfileFieldsChanged = chechIsProfileFieldsChangedInWaiver(isSelectedMemberTheUser, selectedMember);
		if (isProfileFieldsChanged) {
			return true;
		}

		waiverFilled(selectedMember);

		return true;
	};

	const handleOnClickUpdateProfile = () => {
		updateProfile(selectedMember?.id)
			?.then(async () => {
				const userSettingsData = await userApi.getUserSettings_DEPRICATED();
				setUserSettings({
					...userSettingsData.data,
					id: auth.getUserId(),
				});
				waiverFilled(selectedMember);
			})
			.catch(error => {
				console.error(error);
			});
	};

	const displayNoSelectedMemberError = () => {
		setIsNoSelectedMemberError(true);
	};

	return (
		<>
			<LoginDialog
				open={openLoginDialog}
				onClose={handleLoginDialogClose}
				aria-labelledby="form-dialog-title"
				maxWidth="md"
				fullWidth
				css={dialogCss}
				postFromSignUp={handlePostSignup}
			/>

			{isFormInvalid && (
				<Box mb={2}>
					<Alert severity="error">{i18n.waiver.formValidationError}</Alert>
				</Box>
			)}
			<Grid>
				<h1  data-testid="WaiverContent-684F33" css={titleCss(colors)}>{formTitle || i18n.waiver.title}</h1>
			</Grid>
			{questionData && newUserSettings && isLoggedIn ? (
				<Form
					data-aid="WaiverContent-waiverForm"
					id="waiver-form"
					onSubmit={handleSubmitForm}
					render={({ handleSubmit }) => {
						validateForm = handleSubmit;
						return (
							<form data-aid="WaiverContent-form" onSubmit={handleSubmit} encType="multipart/form-data">
								<RowLayout divider>
									<RowLayout.Title title={i18n.waiver.family.subTitle} />
									<RowLayout.Content>
										{isLoggedIn && <FamilyMembers updateSelectedMember={updateSelectedMember} />}
									</RowLayout.Content>
									{isNoSelectedMemberError && !selectedMember && (
										<Box mb={2}>
											<Alert severity="error">{i18n.waiver.family.noSelectedMemberError}</Alert>
										</Box>
									)}
								</RowLayout>
								<div data-aid="WaiverContent-display" onClick={displayNoSelectedMemberError}>
									<div css={disabledCss(!selectedMember)}>
										<RowLayout divider>
											<Box pr={isDesktop && 15} pl={isDesktop && 15}>
												<RowLayout.Title title={i18n.waiver.Questionnaire.title(formTitle)} />
												<RowLayout.Content>
													<Grid container spacing={2}>
														{questionData.map((value, index) => (
															<>
																<Conditional show={value.questionType === QuestionsTypeEnum.ADDRESS}>
																	<QuestionnaireControl
																		data={value}
																		key={index}
																		userBirthDate={selectedMember?.birthDate}
																		userSettings={userSettings}
																		newUserSettings={newUserSettings}
																		setNewUserSettings={setNewUserSettings}
																		isUpdateUserSettings
																	/>
																</Conditional>

																<Conditional show={value.questionType !== QuestionsTypeEnum.ADDRESS}>
																	<Grid item sm={12} md={12}>
																		<div css={questionFormDivCss}>
																			<QuestionnaireControl
																				data={value}
																				key={index}
																				userSettings={userSettings}
																				newUserSettings={newUserSettings}
																				setNewUserSettings={setNewUserSettings}
																				isUpdateUserSettings
																				userBirthDate={selectedMember?.birthDate}
																			/>
																		</div>
																	</Grid>
																</Conditional>
															</>
														))}
													</Grid>
												</RowLayout.Content>
											</Box>
										</RowLayout>
										<RowLayout>
											<Box pr={isDesktop && 15} pl={isDesktop && 15}>
												<RowLayout.Content>
													{termsQuestions &&
														termsQuestions.map((value, key) => (
															<QuestionnaireControl
																data={value}
																flatWaiver
																key={key}
																userSettings={userSettings}
																newUserSettings={newUserSettings}
																setNewUserSettings={setNewUserSettings}
																isUpdateUserSettings
																userBirthDate={selectedMember?.birthDate}
															/>
														))}
												</RowLayout.Content>
											</Box>
										</RowLayout>
										<RowLayout>
											<RowLayout.Content>
												<div css={acceptBtnLayoutCss}>
													<Button
														data-aid="button-WaiverContent-accept"
														css={acceptBtnCss}
														variant="contained"
														color="secondary"
														size="large"
														onClick={handleAcceptBtnSubmit}
													>
														{i18n.buttons.acceptWaiver}
													</Button>
												</div>
											</RowLayout.Content>
										</RowLayout>
									</div>
								</div>
							</form>
						);
					}}
				/>
			) : (
				<Grid container justify="center">
					<CircularProgress />
				</Grid>
			)}

			<ModalWindow isShowing={isAlertProfileChanged} toggle={() => waiverFilled(selectedMember)} padding={0}>
				<ProfileDetailsChangedAlert
					updateProfile={handleOnClickUpdateProfile}
					refusalHandle={() => waiverFilled(selectedMember)}
				/>
			</ModalWindow>
		</>
	);
};
export default WaiverContent;
